import React, { ReactElement, useCallback, useMemo } from 'react'
import {
	IOptionValue,
	FilterDetail,
	IFlattenedAdornment,
	SearchBar,
	ISearchBarLocales,
} from '@copyrightagent/basic-components'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
	SubmittedCaseFilters,
	getDefaultSubmittedFilters,
	STATUS_FILTER_OPTIONS,
	statusLabelKey,
} from 'lib/entities/submittedCaseStatus'

// Search + filter bar for submitted cases. Same component/UX as the /cases list
// (search field + green Search button + funnel popover); the funnel holds the
// derived-status filter. Filters are written to the URL by SearchBar and read
// back in CreateCase.
export default function SubmittedCaseSearchAndFilter(): ReactElement {
	const { t } = useTranslation()
	const history = useHistory()
	const location = useLocation()

	const statusOptions: IOptionValue[] = useMemo(
		() =>
			STATUS_FILTER_OPTIONS.map((option) => ({
				value: option.id,
				label: t(option.labelKey),
			})),
		[t],
	)

	const getFilterDetails = useCallback(
		(
			currentFilters: SubmittedCaseFilters,
		): FilterDetail<SubmittedCaseFilters>[] => [
			{
				filterKey: 'status',
				locales: {
					label: t('createCase/filterStatusLabel'),
					noOptionsAvailable: t('createCase/filterStatusNoOptions'),
					placeholder: t('createCase/filterStatusPlaceholder'),
				},
				filterOptions: statusOptions,
				selectedValue: currentFilters.status,
			},
		],
		[t, statusOptions],
	)

	const setLabelByKey = useCallback(
		(
			key: keyof SubmittedCaseFilters,
			adornment: IFlattenedAdornment<SubmittedCaseFilters>,
		): IFlattenedAdornment<SubmittedCaseFilters> => {
			if (key === 'status') {
				const labelKey = statusLabelKey(adornment.value)
				if (labelKey) adornment.label = t(labelKey)
			}
			return adornment
		},
		[t],
	)

	const locales: ISearchBarLocales = useMemo(
		() => ({
			filterSearchLocales: {
				submitText: t('searchCases/submitText'),
				placeholder: t('createCase/searchPlaceholder'),
			},
			filterPopoverLocales: {
				reset: t('filterCases/reset'),
				apply: t('filterCases/apply'),
			},
		}),
		[t],
	)

	return (
		<SearchBar<SubmittedCaseFilters>
			getDefaultFilters={getDefaultSubmittedFilters}
			setLabelByKey={setLabelByKey}
			getFilterDetails={getFilterDetails}
			locales={locales}
			location={location}
			history={history}
			onSearchSubmit={() => undefined}
		/>
	)
}
